import { memo, useEffect, useMemo, useState } from 'react';
import { Tag } from '@chakra-ui/react';

import type {
  OutletAcbCountResponse,
} from '@app/api/generated';
import { useApi } from '@app/hooks';
import { useVisitGuides } from '@app/hooks/react-native/use-visit-guides';
import { getRoleName } from '@app/utils/jwt';
import { OutletUpdateCounts } from '@app/api/models/acb';
import { canAccessApprovalQueue, updateCount, UPDATE_REQUESTS_CHANGED } from '@app/pages/customer-list/update-outlets/utils/approval';

type CountResponse = OutletAcbCountResponse | ApiResponse<OutletUpdateCounts>;

const resolveCount = (response?: CountResponse, approvalQueue = false): number => {
  if (!response) {
    return 0;
  }
  const { data } = response;
  if (typeof data === 'number') {
    return data;
  }
  return updateCount(data, getRoleName(), approvalQueue);
};

const MenuItemCounterComponent = ({ counterId }: {
  counterId: string
}) => {
  const [value, setValue] = useState(0);
  const { variables } = useVisitGuides();
  const api = useApi();

  const apiMethods = useMemo(() => ({
    'new-outlets': api.acb.getNewCount,
    'updates-outlets': api.acb.getUpdateCount,
    'outlet-approvals': api.acb.getUpdateCount,
    'repairs': api.equipments.getEquipmentsRepairCount
  }), [api]);

  const access: Record<string, string[]> = useMemo(() => {
    if (!variables?.['MENU_COUNTERS_ACCESS']?.value) {return {};}
    try {
     return JSON.parse(variables?.['MENU_COUNTERS_ACCESS']?.value);
    } catch (e) {
      console.log(e);
      return {};
    }
  }, [variables]);

  const canView: boolean = useMemo(() => {
    const roles = access?.[counterId];
    const roleName = getRoleName();
    if (counterId === 'outlet-approvals') { return canAccessApprovalQueue(roleName); }
    return !!roleName && !!roles?.includes(roleName);
  }, [access, counterId]);

  useEffect(() => {
    if (!canView) { return; }

    let active = true;
    let request = 0;
    const refresh = async () => {
      const current = ++request;
      try {
        const response = await apiMethods[counterId]?.();
        if (active && current === request) {
          setValue(resolveCount(response as CountResponse | undefined, counterId === 'outlet-approvals'));
        }
      } catch {
        // Сохраняем последнее значение; следующая попытка — по таймеру.
      }
    };
    void refresh();
    const timerId = setInterval(refresh, 60_000);
    window.addEventListener(UPDATE_REQUESTS_CHANGED, refresh);
    return () => {
      active = false;
      clearInterval(timerId);
      window.removeEventListener(UPDATE_REQUESTS_CHANGED, refresh);
    };
  }, [apiMethods, canView, counterId]);
  
  if (!canView || !value) {return null;}

  return <Tag >{value}</Tag>;
};

export const MenuItemCounter = memo(MenuItemCounterComponent);
